import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './authContext';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { CircularProgress } from '@mui/material';
import { GoogleIcon, AppleIcon, PersonIcon, EmailIcon, LockIcon, EyeIcon } from './authIcons';
import { signInWithProvider } from './oauthPopup';

const CreateAccount = () => {
  const { user, shopikUser, customer, loading } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [signingUp, setSigningUp] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && customer && Object.keys(customer).length > 0) {
      navigate('/products-v2/' + user?.uid);
    } else if (!loading && shopikUser && Object.keys(shopikUser).length > 0) {
      navigate('/customer-select');
    }
  }, [loading, user, customer, shopikUser, navigate]);

  const signUpWithGoogle = () => {
    setError('');
    setSigningUp(true);
    signInWithProvider(auth, 'google').catch((error) => {
      console.error(error);
      setError(error.message);
      setSigningUp(false);
    });
  };

  const signUpWithApple = () => {
    setError('');
    setSigningUp(true);
    signInWithProvider(auth, 'apple').catch((error) => {
      console.error(error);
      setError(error.message);
      setSigningUp(false);
    });
  };

  const friendlyAuthError = (error) => {
    switch (error.code) {
      case 'auth/email-already-in-use':
        return 'An account with that email already exists. Try signing in instead.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/weak-password':
        return 'Password must be at least 6 characters.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  const signUpWithPassword = async () => {
    setError('');
    if (!fullName || !email || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSigningUp(true);
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(credential.user, { displayName: fullName });
      // Leave signingUp true: the redirect effect fires once authContext finishes
      // creating the Stripe customer/Firestore docs, which is slow for new accounts.
    } catch (error) {
      console.error('❌ Error creating account:', error);
      setError(friendlyAuthError(error));
      setSigningUp(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="images/logo.png" loading="lazy" alt="" />
        </div>
        <h1 className="auth-heading">Create your account</h1>
        <p className="auth-subheading">Get started with Hemlock</p>

        <button className="auth-oauth-button" onClick={signUpWithGoogle} disabled={signingUp}>
          {signingUp ? <CircularProgress size={20} /> : <><GoogleIcon /> Sign up with Google</>}
        </button>
        <button className="auth-oauth-button" onClick={signUpWithApple} disabled={signingUp}>
          {signingUp ? <CircularProgress size={20} /> : <><AppleIcon /> Sign up with Apple</>}
        </button>

        <div className="auth-divider">OR</div>

        <div className="auth-field">
          <span className="auth-field-icon"><PersonIcon /></span>
          <input
            type="text"
            placeholder="Full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            disabled={signingUp}
          />
        </div>
        <div className="auth-field">
          <span className="auth-field-icon"><EmailIcon /></span>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={signingUp}
          />
        </div>
        <div className="auth-field">
          <span className="auth-field-icon"><LockIcon /></span>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={signingUp}
          />
          <button type="button" className="auth-field-toggle" onClick={() => setShowPassword(!showPassword)} aria-label="Toggle password visibility" disabled={signingUp}>
            <EyeIcon visible={showPassword} />
          </button>
        </div>
        <div className="auth-field">
          <span className="auth-field-icon"><LockIcon /></span>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={signingUp}
          />
          <button type="button" className="auth-field-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)} aria-label="Toggle confirm password visibility" disabled={signingUp}>
            <EyeIcon visible={showConfirmPassword} />
          </button>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <button
          className="auth-submit-button"
          onClick={signUpWithPassword}
          disabled={signingUp}
        >
          {signingUp ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create Account'}
        </button>

        <p className="auth-terms">
          By creating an account, you agree to our<br />
          <Link to="/terms">Terms of Service</Link> and <Link to="/terms">Privacy Policy.</Link>
        </p>

        <div className="auth-footer">
          Already have an account?{' '}
          <Link
            to="/login"
            className="auth-footer-link"
            style={signingUp ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
            tabIndex={signingUp ? -1 : undefined}
          >
            Sign in instead
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
